/**
 * The function „Odstoupit od smlouvy zde“ (BEST-384, ADR 0012, sections/bestio-withdrawal-form.liquid).
 *
 * Two steps without a sign-in, against the public withdrawals API of the Platform, without cookies:
 *   POST /api/public/v1/withdrawals/lookup  200 {orderName, orderedAt, items} | 422 withdrawal_order_not_found | 422 withdrawal_invalid | 429 | 503
 *   POST /api/public/v1/withdrawals         201 {reference, receivedAt, orderName} | 200 the same again | 409 | 422 | 429 withdrawal_limit | 503
 * The server verifies the order and decides the time of the receipt; the client checks the same limits only to answer
 * sooner. A submission id per statement makes a double click, a reload or a retry after a lost answer one withdrawal; it
 * lives in sessionStorage until the receipt. When the API cannot be reached, the form offers the e-mail with the statement
 * filled in and the post, which always stay open. The decisions (createWithdrawalFlow) are separate from the page
 * (createDomView), so the contract test drives them with a fake fetch and a recording view. The API call, the submission
 * ids and the page context are shared with the complaint form (bestio-public-form.js, loaded first).
 */
(() => {
  const {format, text, NetworkError, createApi, outcomeOf: publicOutcome, createSubmissions: publicSubmissions} = window.BestioPublicForm;
  const API_PATH = '/api/public/v1/withdrawals';
  const STORAGE_KEY = 'bestio-withdrawal-submissions';
  const LIMITS = {orderName: 12, email: 254, customerName: 120, bankAccount: 64};

  /** What an answer means for the form; the codes are the API's (PublicWithdrawalsApi). */
  const outcomeOf = (status, data) => publicOutcome(status, data, 'withdrawal');

  /** The first step as the API takes it, or the field errors (field → code). */
  function validateDetails(values) {
    const value = {
      orderName: text(values.orderName).trim(),
      email: text(values.email).trim(),
      customerName: text(values.customerName).trim().replace(/\s+/g, ' '),
      bankAccount: text(values.bankAccount).trim().replace(/\s+/g, ' '),
    };
    const errors = {};
    if (!value.orderName) errors.orderName = 'required';
    else if (!/^#?\d{1,9}$/.test(value.orderName)) errors.orderName = 'order';
    if (!value.email) errors.email = 'required';
    else if (value.email.length > LIMITS.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) errors.email = 'email';
    // eslint-disable-next-line no-control-regex
    const control = /[\u0000-\u001f\u007f]/;
    if (!value.customerName) errors.customerName = 'required';
    else if (value.customerName.length > LIMITS.customerName) errors.customerName = 'too_long';
    else if (control.test(value.customerName)) errors.customerName = 'name';
    if (value.bankAccount && (value.bankAccount.length > LIMITS.bankAccount || !/^[A-Za-z0-9 /-]+$/.test(value.bankAccount))) {
      errors.bankAccount = 'account';
    }
    return {value, errors};
  }

  /** The order of the lookup as the form uses it, or null when the answer is not the contract (shown as a failure). */
  function normalizeOrder(data) {
    if (!data || typeof data !== 'object' || !Array.isArray(data.items) || typeof data.orderName !== 'string') return null;
    return {
      orderName: data.orderName,
      orderedAt: text(data.orderedAt),
      items: data.items
        .filter(item => item && typeof item.lineItemId === 'string' && typeof item.title === 'string'
          && Number.isInteger(item.quantity) && item.quantity > 0)
        .map(item => ({lineItemId: item.lineItemId, title: item.title, variantTitle: text(item.variantTitle) || null, quantity: item.quantity})),
    };
  }

  /**
   * The declared goods: every item ticked in full is the whole order (no list), otherwise the ticked items with their
   * pieces. Nothing ticked is an error, never a silent withdrawal from everything.
   */
  function declaredItems(order, selection) {
    const chosen = order.items
      .map(item => ({item, pick: selection.find(entry => entry.lineItemId === item.lineItemId)}))
      .filter(({pick}) => pick?.checked);
    if (order.items.length > 0 && chosen.length === 0) return {error: 'nothing_selected'};
    const whole = chosen.length === order.items.length
      && chosen.every(({item, pick}) => Number(pick.quantity ?? item.quantity) === item.quantity);
    if (whole) return {items: null};
    return {
      items: chosen.map(({item, pick}) => ({
        lineItemId: item.lineItemId,
        quantity: Math.min(item.quantity, Math.max(1, Number(pick.quantity ?? item.quantity) || 1)),
      })),
    };
  }

  /** The statement as one stable text, so the same statement sent again reuses its submission id. */
  const fingerprint = (details, items) => JSON.stringify([details.orderName.replace(/^#/, ''), details.email.toLowerCase(),
    details.customerName, details.bankAccount, items]);

  /** Submission ids by statement until the receipt (bestio-public-form.js). */
  const createSubmissions = ({storage, uuid}) => publicSubmissions({storage, storageKey: STORAGE_KEY, uuid});

  /** The e-mail the customer can send instead, with the statement filled in. */
  function mailtoHref(supportEmail, t, details) {
    const params = {order: details.orderName || '…', name: details.customerName || '…', account: details.bankAccount || '—'};
    return `mailto:${supportEmail}?subject=${encodeURIComponent(format(t('mail_subject'), params))}`
      + `&body=${encodeURIComponent(format(t('mail_body'), params))}`;
  }

  /** Field errors of a 422 in the form's fields; an item error points at the goods. */
  function serverFieldErrors(data) {
    const errors = {};
    for (const [key, codes] of Object.entries(data?.errors || {})) {
      const code = Array.isArray(codes) ? codes[0] : '';
      if (key.startsWith('items')) errors.items = 'nothing_selected';
      else if (['orderName', 'email', 'customerName', 'bankAccount'].includes(key)) {
        errors[key] = code === 'required' ? 'required' : code === 'too_long' ? 'too_long'
          : {orderName: 'order', email: 'email', customerName: 'name', bankAccount: 'account'}[key];
      }
    }
    return errors;
  }

  function createWithdrawalFlow({api, view, submissions, supportEmail, t, formatDate}) {
    const state = {details: null, order: null, busy: false};

    // Every refusal that leaves the customer without an online receipt offers the e-mail with the statement filled in:
    // a mistyped order, the daily limit of the order, an outage or too many tries. The right to withdraw never hangs on
    // the form.
    const failure = (kind, details) => view.showMessage(kind, {
      fallback: ['failed', 'rate_limited', 'not_found', 'order_limit'].includes(kind),
      mailto: mailtoHref(supportEmail, t, details || state.details || {}),
    });

    async function lookup(values) {
      if (state.busy) return;
      const {value, errors} = validateDetails(values);
      view.showFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;
      state.details = value;
      state.busy = true;
      view.setBusy('details', true);
      try {
        const response = await api.lookup({orderName: value.orderName, email: value.email});
        const outcome = outcomeOf(response.status, response.data);
        const order = outcome === 'ok' ? normalizeOrder(response.data) : null;
        if (order) {
          state.order = order;
          view.showSummary({
            order: format(t('summary_order'), {order: order.orderName, date: formatDate(order.orderedAt, false)}),
            statement: format(t('statement'), {order: order.orderName}),
            consumer: format(t('consumer'), {name: value.customerName}),
            account: value.bankAccount ? format(t('account'), {account: value.bankAccount}) : '',
            items: order.items,
          });
        } else if (outcome === 'invalid') view.showFieldErrors(serverFieldErrors(response.data));
        else failure(outcome === 'ok' ? 'failed' : outcome, value);
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error;
        failure('failed', value);
      } finally {
        state.busy = false;
        view.setBusy('details', false);
      }
    }

    async function confirm(selection) {
      if (state.busy || !state.order) return;
      const declared = declaredItems(state.order, selection);
      if (declared.error) {
        view.showItemsError(declared.error);
        return;
      }
      view.showItemsError(null);
      const details = state.details;
      const key = fingerprint(details, declared.items);
      const body = {
        submissionId: submissions.idFor(key), orderName: details.orderName, email: details.email,
        customerName: details.customerName, bankAccount: details.bankAccount || null, items: declared.items,
      };
      state.busy = true;
      view.setBusy('summary', true);
      try {
        const response = await api.submit(body);
        const outcome = outcomeOf(response.status, response.data);
        if (outcome === 'ok' && response.data && typeof response.data.reference === 'string') {
          submissions.forget(key);
          view.showDone(format(t(response.status === 201 ? 'done_text' : 'done_again'), {
            order: text(response.data.orderName) || state.order.orderName,
            date: formatDate(text(response.data.receivedAt), true),
            reference: response.data.reference,
          }));
          return;
        }
        if (outcome === 'conflict') {
          // Another statement went under this id before (a changed form after a lost answer); the next try is a new one.
          submissions.renew(key);
          failure('failed', details);
        } else if (outcome === 'invalid') {
          const errors = serverFieldErrors(response.data);
          if (errors.items) view.showItemsError('nothing_selected');
          else {
            view.showDetails();
            view.showFieldErrors(errors);
          }
        } else failure(outcome === 'ok' ? 'failed' : outcome, details);
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error;
        failure('failed', details);
      } finally {
        state.busy = false;
        view.setBusy('summary', false);
      }
    }

    function back() {
      state.order = null;
      view.showDetails();
    }

    return {lookup, confirm, back, state};
  }

  function createDomView(root, t) {
    const q = selector => root.querySelector(selector);
    const parts = {
      form: q('[data-withdrawal-form]'),
      continue: q('[data-withdrawal-continue]'),
      message: q('[data-withdrawal-message]'),
      messageText: q('[data-withdrawal-message-text]'),
      fallback: q('[data-withdrawal-fallback]'),
      fallbackText: q('[data-withdrawal-fallback-text]'),
      mailto: q('[data-withdrawal-mailto]'),
      summary: q('[data-withdrawal-summary]'),
      summaryOrder: q('[data-withdrawal-summary-order]'),
      items: q('[data-withdrawal-items]'),
      itemsError: q('[data-withdrawal-error="items"]'),
      statement: q('[data-withdrawal-statement]'),
      consumer: q('[data-withdrawal-consumer]'),
      account: q('[data-withdrawal-account]'),
      confirm: q('[data-withdrawal-confirm]'),
      back: q('[data-withdrawal-back]'),
      done: q('[data-withdrawal-done]'),
      doneText: q('[data-withdrawal-done-text]'),
      template: q('[data-withdrawal-item-template]'),
    };
    const field = name => q(`[data-withdrawal-field="${name}"]`);
    const errorOf = name => q(`[data-withdrawal-error="${name}"]`);
    const errorText = code => t(`error_${code}`) || t('error_required');

    return {
      values: () => Object.fromEntries(['orderName', 'email', 'customerName', 'bankAccount'].map(name => [name, field(name)?.value ?? ''])),
      showFieldErrors(errors) {
        let first = null;
        for (const name of ['orderName', 'email', 'customerName', 'bankAccount']) {
          const input = field(name);
          const node = errorOf(name);
          const code = errors[name];
          if (node) {
            node.hidden = !code;
            node.textContent = code ? errorText(code) : '';
          }
          if (input) {
            if (code) input.setAttribute('aria-invalid', 'true');
            else input.removeAttribute('aria-invalid');
          }
          if (code && !first) first = input;
        }
        if (first) first.focus();
      },
      showMessage(kind, {fallback, mailto}) {
        parts.messageText.textContent = t(kind) || t('failed');
        parts.fallback.hidden = !fallback;
        parts.fallbackText.textContent = t('fallback');
        parts.mailto.href = mailto;
        parts.message.hidden = false;
        parts.message.focus();
      },
      showSummary({order, statement, consumer, account, items}) {
        parts.message.hidden = true;
        parts.summaryOrder.textContent = order;
        parts.statement.textContent = statement;
        parts.consumer.textContent = consumer;
        parts.account.textContent = account;
        parts.account.hidden = !account;
        parts.items.replaceChildren();
        if (items.length === 0) {
          const li = document.createElement('li');
          li.textContent = t('whole_order');
          parts.items.append(li);
        }
        for (const item of items) {
          const node = parts.template.content.firstElementChild.cloneNode(true);
          node.dataset.lineItemId = item.lineItemId;
          node.querySelector('[data-withdrawal-item-title]').textContent = item.variantTitle ? `${item.title} (${item.variantTitle})` : item.title;
          const select = node.querySelector('[data-withdrawal-item-quantity]');
          if (item.quantity > 1) {
            select.hidden = false;
            select.setAttribute('aria-label', format(t('item_quantity'), {title: item.title}));
            for (let count = item.quantity; count >= 1; count -= 1) {
              const option = document.createElement('option');
              option.value = String(count);
              option.textContent = String(count);
              select.append(option);
            }
          }
          parts.items.append(node);
        }
        parts.itemsError.hidden = true;
        parts.form.hidden = true;
        parts.summary.hidden = false;
        parts.summary.focus();
      },
      selection: () => [...parts.items.querySelectorAll('[data-line-item-id]')].map(node => ({
        lineItemId: node.dataset.lineItemId,
        checked: node.querySelector('[data-withdrawal-item-check]').checked,
        quantity: Number(node.querySelector('[data-withdrawal-item-quantity]').value) || undefined,
      })),
      showItemsError(code) {
        parts.itemsError.hidden = !code;
        parts.itemsError.textContent = code ? t(code) : '';
        if (code) parts.itemsError.focus?.();
      },
      showDetails() {
        parts.summary.hidden = true;
        parts.message.hidden = true;
        parts.form.hidden = false;
        field('orderName')?.focus();
      },
      showDone(message) {
        parts.message.hidden = true;
        parts.summary.hidden = true;
        parts.form.hidden = true;
        parts.doneText.textContent = message;
        parts.done.hidden = false;
        parts.done.focus();
      },
      setBusy(step, busy) {
        const button = step === 'details' ? parts.continue : parts.confirm;
        button.disabled = busy;
        button.textContent = t(busy ? (step === 'details' ? 'checking' : 'sending') : (step === 'details' ? 'continue' : 'confirm'));
        parts.back.disabled = busy;
        (step === 'details' ? parts.form : parts.summary).setAttribute('aria-busy', busy ? 'true' : 'false');
      },
      bind({onLookup, onConfirm, onBack}) {
        parts.form.addEventListener('submit', event => {
          event.preventDefault();
          onLookup();
        });
        parts.confirm.addEventListener('click', () => onConfirm());
        parts.back.addEventListener('click', () => onBack());
      },
      ready() { parts.form.hidden = false; },
    };
  }

  function start(root) {
    const {t, formatDate, storage, uuid, fetchImpl} = window.BestioPublicForm.pageContext(root, '[data-withdrawal-i18n]');
    const view = createDomView(root, t);
    const flow = createWithdrawalFlow({
      api: createApi({baseUrl: root.dataset.apiBaseUrl, path: API_PATH, fetchImpl}),
      view,
      submissions: createSubmissions({storage, uuid}),
      supportEmail: root.dataset.supportEmail || '',
      t,
      formatDate,
    });
    view.bind({
      onLookup: () => flow.lookup(view.values()),
      onConfirm: () => flow.confirm(view.selection()),
      onBack: () => flow.back(),
    });
    view.ready();
    window.BestioPublicForm.prefillLinkedOrder(root, window.location.hash);
  }

  window.BestioWithdrawalForm = {
    createApi: options => createApi({path: API_PATH, ...options}), outcomeOf, validateDetails, normalizeOrder, declaredItems,
    createSubmissions, mailtoHref, serverFieldErrors, createWithdrawalFlow, format,
  };
  if (typeof document === 'undefined') return;
  const root = document.querySelector('[data-bestio-withdrawal]');
  if (root) start(root);
})();
